import { CREDIT_CONTROLLER_ASSIGNEE, UNASSIGNED_ASSIGNEE } from '@/constants/dispute-types';

export interface AssigneeCandidate {
  id: string;
  firstName: string;
  lastName: string;
}

export interface StageAssignee {
  /** Explicit user configured on the stage, if any. */
  assignedToUserId?: number;
  /** Stage is configured to use the customer's credit controller; the backend resolves the user. */
  usesCreditController: boolean;
}

/**
 * Reads the assigned user configured on a workflow stage's serialised properties.
 * A `CREDIT_CONTROLLER` sentinel is not resolved client-side: the backend assigns
 * the customer's credit controller when the dispute is created or moved to the level.
 */
export function readStageAssignee(
  properties: string | null | undefined,
  users: AssigneeCandidate[] = [],
): StageAssignee {
  if (!properties) {
    return { usesCreditController: false };
  }

  let assignedUser: unknown;
  try {
    assignedUser = JSON.parse(properties)?.allowedAssigneeRoles?.[0];
  } catch {
    return { usesCreditController: false };
  }

  if (typeof assignedUser !== 'string' || assignedUser === '' || assignedUser === UNASSIGNED_ASSIGNEE) {
    return { usesCreditController: false };
  }

  if (assignedUser === CREDIT_CONTROLLER_ASSIGNEE) {
    return { usesCreditController: true };
  }

  const parsed = parseInt(assignedUser, 10);
  if (!Number.isNaN(parsed)) {
    return { assignedToUserId: parsed, usesCreditController: false };
  }

  const matchedUser = users.find((u) => `${u.firstName} ${u.lastName}` === assignedUser);
  return {
    assignedToUserId: matchedUser ? parseInt(matchedUser.id, 10) : undefined,
    usesCreditController: false,
  };
}

/**
 * Resolves the user a dispute starts assigned to from its level 1 stage properties,
 * substituting the customer's credit controller for a `CREDIT_CONTROLLER` sentinel.
 * Returns undefined when the configured user is not one of the company's active users.
 */
export function resolveLevelOneAssignee(
  properties: string | null | undefined,
  activeUsers: AssigneeCandidate[],
  customerCreditControllerId?: string,
): number | undefined {
  const { assignedToUserId, usesCreditController } = readStageAssignee(properties, activeUsers);

  const candidateId = usesCreditController ? parseInt(customerCreditControllerId ?? '', 10) : assignedToUserId;

  if (candidateId === undefined || Number.isNaN(candidateId)) {
    return undefined;
  }

  return activeUsers.some((u) => parseInt(u.id, 10) === candidateId) ? candidateId : undefined;
}
